import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { useViewerStudent } from "../../auth/ViewAsContext";

/**
 * /view-as/:studentId/summary — the student's project as a paper.
 *
 * A mentor opening the mirror used to land on the student's dashboard: a
 * greeting, a progress bar, a next-step card. Useful for "what do they see",
 * useless for "what have they produced". This is the other thing — every
 * step's approved output, in the order a paper puts them, read top to bottom.
 *
 * Section headings are the drill-down: clicking one opens that step inside the
 * same mirror. No mentor commentary and no buttons in the body — if a mentor
 * needs the detail, that is what the click is for.
 *
 * The Introduction is AI-written from the Step 3 background research and is
 * labelled as such, every time it appears. The rest is the student's own
 * words, verbatim. That line must not blur: the product's whole premise is
 * that the work is theirs.
 */

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL ?? "";
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY ?? "";

/* 'completed' is how Step 1 records approval; every other step says
   'approved_draft'. Anything else is the student still working. */
const APPROVED = new Set(["completed", "approved_draft"]);

type Mark = "approved" | "draft" | "none";

interface Section {
  step: number;
  heading: string;
  mark: Mark;
  /* [label, text] — label is omitted when a section has a single body. */
  blocks: [string | null, string][];
}

const clip = (s: string, n = 420) => {
  const t = (s ?? "").trim();
  return t.length > n ? t.slice(0, n).replace(/\s+\S*$/, "") + "…" : t;
};

export default function ViewAsSummaryPage() {
  const { studentId, studentName, viewerIsStudent, viewerIsParent, prefixPath, loading: vasLoading } =
    useViewerStudent();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  const [meta, setMeta] = useState<string>("");
  const [sections, setSections] = useState<Section[]>([]);
  const [projectId, setProjectId] = useState<string | null>(null);

  const [abstract, setAbstract] = useState<string | null>(null);
  const [abstractAt, setAbstractAt] = useState<string | null>(null);
  const [abstractBusy, setAbstractBusy] = useState(false);
  const [abstractErr, setAbstractErr] = useState<string | null>(null);

  const loadAbstract = useCallback(
    async (pid: string, force = false) => {
      setAbstractBusy(true);
      setAbstractErr(null);
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const res = await fetch(`${SUPABASE_URL}/functions/v1/generate-abstract`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: SUPABASE_ANON_KEY,
            Authorization: `Bearer ${session?.access_token ?? ""}`,
          },
          body: JSON.stringify({ project_id: pid, force }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.ok === false) {
          throw new Error(data?.error ?? `Request failed (${res.status})`);
        }
        setAbstract(data.body);
        setAbstractAt(data.generated_at ?? null);
      } catch (e) {
        setAbstractErr((e as Error).message);
      } finally {
        setAbstractBusy(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (vasLoading || !studentId) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);

      const { data: projects, error: pErr } = await supabase
        .from("project")
        .select("project_id, current_step, track")
        .eq("student_id", studentId)
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .limit(1);
      if (cancelled) return;
      if (pErr) {
        setError(pErr.message);
        setLoading(false);
        return;
      }
      const project = projects?.[0];
      if (!project) {
        setError("This student has no active project.");
        setLoading(false);
        return;
      }
      const pid = project.project_id as string;
      setProjectId(pid);

      const [prof, topic, question, background, hypothesis, design] =
        await Promise.all([
          supabase
            .from("student_profile")
            .select("grade, school_name")
            .eq("student_id", studentId)
            .maybeSingle(),
          supabase
            .from("research_topic")
            .select("title, status, selected_recommendation_id")
            .eq("project_id", pid)
            .maybeSingle(),
          supabase
            .from("research_question")
            .select("draft_payload, track_rationale, status")
            .eq("project_id", pid)
            .maybeSingle(),
          supabase
            .from("background_research")
            .select("payload, status")
            .eq("project_id", pid)
            .maybeSingle(),
          supabase
            .from("hypothesis_design")
            .select("payload, status")
            .eq("project_id", pid)
            .maybeSingle(),
          supabase
            .from("experiment_design")
            .select("payload, status")
            .eq("project_id", pid)
            .maybeSingle(),
        ]);
      if (cancelled) return;

      /* research_topic.title is null on every row in the system: Step 1 stores
         the chosen topic on the topic_recommendation it points at, and keeps
         only the decision here. Read through the link, or the section is
         empty for everyone. */
      const recId = topic.data?.selected_recommendation_id as string | undefined;
      const { data: rec } = recId
        ? await supabase
            .from("topic_recommendation")
            .select("title, description")
            .eq("id", recId)
            .maybeSingle()
        : { data: null };
      if (cancelled) return;

      const p = prof.data;
      setMeta(
        [
          studentName,
          p?.grade ? `Grade ${p.grade}` : null,
          p?.school_name,
          `Step ${project.current_step} of 8`,
        ]
          .filter(Boolean)
          .join(" · "),
      );

      const mark = (row: { status?: string | null } | null | undefined): Mark =>
        !row ? "none" : APPROVED.has(row.status ?? "") ? "approved" : "draft";

      const bgP = (background.data?.payload ?? {}) as Record<string, string>;
      const hyP = (hypothesis.data?.payload ?? {}) as Record<string, unknown>;
      const deP = (design.data?.payload ?? {}) as Record<string, string>;

      /* Step 2 keeps the refined question inside a jsonb draft; fall back to
         the background copy, which Step 3 carries forward. */
      const qPayload = (question.data?.draft_payload ?? {}) as Record<string, unknown>;
      const refined =
        (typeof qPayload.refined_question === "string" && qPayload.refined_question) ||
        bgP.refined_question ||
        "";

      /* An engineering project does not ask a question — it states a problem.
         Step 2 stores it as problem_statement, which is the same field step3
         reads when it carries Step 2 forward. */
      const isEngineering = project.track === "engineering_design";
      const problemStatement =
        typeof qPayload.problem_statement === "string" ? qPayload.problem_statement : "";
      const step2Text = isEngineering ? problemStatement : refined;

      const blocks = (...pairs: [string | null, string | undefined][]) =>
        pairs
          .map(([l, v]) => [l, (v ?? "").trim()] as [string | null, string])
          .filter(([, v]) => v);

      const hyBlocks = Array.isArray(hyP.blocks)
        ? (hyP.blocks as Record<string, string>[])
        : [];

      const built: Section[] = [
        {
          step: 1,
          heading: "Research Topic",
          mark: mark(topic.data),
          /* The topic is its title, and nothing else. The paragraph that used
             to sit under it described what the project investigates and why it
             matters — which is exactly what Research Question and Background
             say, in the student's own words and at the right length. "Why they
             chose it" went for the same reason: a paper does not open by
             explaining what drew the author to the subject. Both are still on
             Step 1 for anyone who wants them. */
          blocks: blocks([null, rec?.title ?? topic.data?.title ?? undefined]),
        },
        {
          step: 2,
          heading: isEngineering ? "Problem Statement" : "Research Question",
          mark: mark(question.data),
          blocks: blocks([null, step2Text]),
        },
        {
          step: 3,
          heading: "Background",
          mark: mark(background.data),
          blocks: blocks(
            ["Purpose", bgP.purpose],
            ["What the research shows", bgP.what_research_shows],
            ["The gap", bgP.the_gap],
            ["Why it matters", bgP.significance],
          ),
        },
        {
          step: 4,
          heading: "Hypothesis",
          mark: mark(hypothesis.data),
          blocks: blocks(
            ["Independent variable", hyP.iv as string],
            ["Controlled variables", hyP.cv as string],
            ...hyBlocks.map(
              (b, i) =>
                [
                  hyBlocks.length > 1 ? `Prediction ${i + 1}` : "Prediction",
                  b?.hypothesis,
                ] as [string, string | undefined],
            ),
          ),
        },
        {
          step: 5,
          heading: "Method",
          mark: mark(design.data),
          blocks: blocks(
            ["Design", deP.design_type],
            ["Materials", deP.materials],
            ["Procedure", deP.procedure],
            ["Analysis plan", deP.analysis_plan],
          ),
        },
        { step: 6, heading: "Data", mark: "none", blocks: [] },
        { step: 7, heading: "Results and Analysis", mark: "none", blocks: [] },
        { step: 8, heading: "Conclusion", mark: "none", blocks: [] },
      ];

      /* Prefer the refined research question as the paper's title — it is the
         sharpest statement of the study. The Step 1 topic is the fallback. */
      setTitle(refined || rec?.title || topic.data?.title || null);
      setSections(built);
      setLoading(false);

      /* The abstract is cached server-side; this returns the stored one unless
         the student's work has changed since. Mentors only — the function
         refuses a student's session anyway, and asking would spend a request
         to be told no, and leave a 403 in their console. */
      if (!viewerIsStudent) loadAbstract(pid);
    })();

    return () => {
      cancelled = true;
    };
  }, [studentId, studentName, vasLoading, viewerIsStudent, loadAbstract]);

  if (vasLoading || loading) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="info">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, sm: 4 }, py: 4, maxWidth: 780, mx: "auto" }}>
      {/* ── Title block ─────────────────────────────────────────────── */}
      <Typography
        variant="h5"
        sx={{ fontWeight: 800, lineHeight: 1.3, textAlign: "center" }}
      >
        {title ?? "Untitled project"}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: "center", mt: 1 }}
      >
        {meta}
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* ── Abstract — AI-written, and always said so ─────────────────
          Mentors only. To the student it would be a polished model answer to
          the very thing Step 8 asks them to write, sitting on top of their own
          project — the most copyable thing we could put in front of them. */}
      {!viewerIsStudent && (
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="overline"
            sx={{ fontWeight: 700, letterSpacing: 1 }}
          >
            Abstract
          </Typography>
          <Box sx={{ flex: 1 }} />
          <AutoAwesomeIcon sx={{ fontSize: 14, color: "text.disabled" }} />
          <Typography variant="caption" color="text.secondary">
            AI-written from the work completed
            {abstractAt ? ` · ${new Date(abstractAt).toLocaleDateString()}` : ""}
          </Typography>
          {projectId && (
            <Button
              size="small"
              startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
              disabled={abstractBusy}
              onClick={() => loadAbstract(projectId, true)}
            >
              {abstractBusy ? "Writing…" : "Rewrite"}
            </Button>
          )}
        </Box>

        {abstractErr && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {abstractErr}
          </Alert>
        )}
        {abstractBusy && !abstract && (
          <Typography variant="body2" color="text.secondary">
            Reading the work completed so far…
          </Typography>
        )}
        {abstract &&
          abstract.split("\n").map(
            (para, i) =>
              para.trim() && (
                <Typography
                  key={i}
                  variant="body2"
                  sx={{ lineHeight: 1.8, mb: 1.5 }}
                >
                  {para.trim()}
                </Typography>
              ),
          )}
      </Box>
      )}

      {/* ── The student's own work, section by section ───────────────── */}
      {sections.map((s) => {
        /* A parent sees approved work only. Everything else is named and left
           closed — "Started" once there is writing, "To be started" before.

           reached MUST be computed from the mark, not from blocks.length:
           blocks are emptied for a parent just below, and deriving it from the
           emptied array would label every section "To be started" and make an
           active project look untouched. */
        const hasWork = s.blocks.length > 0;
        const showBlocks = viewerIsParent ? s.mark === "approved" : hasWork;
        const reached = viewerIsParent ? s.mark !== "none" && hasWork : hasWork;
        const blocks = showBlocks ? s.blocks : [];
        return (
          <Box key={s.step} sx={{ mb: reached ? 3.5 : 1.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 1,
                mb: reached ? 1 : 0,
              }}
            >
              {/* The heading opens the step workspace, which a parent cannot
                  load — so for them it is a label, not a link. */}
              <Typography
                variant="overline"
                onClick={
                  viewerIsParent
                    ? undefined
                    : () => navigate(prefixPath(`/step/${s.step}`))
                }
                sx={{
                  fontWeight: 700,
                  letterSpacing: 1,
                  cursor: viewerIsParent ? "default" : "pointer",
                  color: reached ? "text.primary" : "text.disabled",
                  textDecoration: "underline",
                  textDecorationColor: "transparent",
                  transition: "text-decoration-color 120ms",
                  "&:hover": viewerIsParent
                    ? undefined
                    : { textDecorationColor: "currentColor" },
                }}
              >
                {s.heading}
              </Typography>
              <Box sx={{ flex: 1 }} />
              {/* "draft" is mentor vocabulary for "not approved". To the
                  student whose writing it is, that reads as a verdict — so
                  they get the same fact said plainly instead. */}
              {s.mark === "draft" && (
                <Typography variant="caption" color="warning.main">
                  {viewerIsParent
                    ? "Started"
                    : viewerIsStudent
                      ? "awaiting review"
                      : "draft"}
                </Typography>
              )}
              {!reached && s.mark !== "draft" && (
                <Typography variant="caption" color="text.disabled">
                  {viewerIsParent
                    ? "To be started"
                    : viewerIsStudent
                      ? "not started"
                      : "not yet"}
                </Typography>
              )}
            </Box>

            {blocks.map(([label, text], i) => (
              <Box key={i} sx={{ mb: 1.25 }}>
                {label && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", fontWeight: 600 }}
                  >
                    {label}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ lineHeight: 1.75 }}>
                  {clip(text)}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      })}
    </Box>
  );
}
